<template>
  <v-card elevation="0" class="mt-2">
    <v-toolbar color="primary" dark dense>
      <v-icon left>fa-chart-bar</v-icon>
      <v-toolbar-title style="font-size:15px; font-weight:500">
        Dashboard — Solicitudes de Beca
      </v-toolbar-title>
      <div class="mt-6 ml-3" style="width: 90px">
        <v-autocomplete
          :value="ciclo"
          label="Ciclo"
          :items="ciclos"
          no-data-text="Sin resultados"
          @input="$emit('update:ciclo', $event)"
        >
        </v-autocomplete>
      </div>
    </v-toolbar>

    <v-container fluid>

      <!-- TARJETA DESTACADA: Listas para reaplicación -->
      <v-row class="mt-2 mb-1">
        <v-col cols="12">
          <v-skeleton-loader v-if="loading" type="card" height="90" />
          <v-card v-else dark class="pa-4 d-flex align-center" style="border-radius:10px; background:#6C1C19">
            <v-icon size="44" class="mr-4">fa-redo-alt</v-icon>
            <div>
              <div style="font-size:12px; opacity:0.9; font-weight:500; letter-spacing:.5px">
                LISTAS PARA EVALUACION Y REAPLICACIÓN DEL SIGUIENTE CICLO 
              </div>
              <div style="font-size:40px; font-weight:700; line-height:1.1">
                {{ conteo.aprobado }}
              </div>
              <div style="font-size:12px; opacity:0.85">
                Solicitudes aprobadas que se enviarán en la próxima reaplicación
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- KPIs por estado -->
      <v-row>
        <v-col v-for="kpi in kpiCards" :key="kpi.label" cols="6" sm="4" md="3" lg="2">
          <v-skeleton-loader v-if="loading" type="card" height="80" />
          <v-card v-else class="pa-3" :style="{ background: kpi.bgColor, color: kpi.textColor, border: '1px solid rgba(0,0,0,0.12)' }">
            <div class="text-subtitle-2">{{ kpi.label }}</div>
            <div class="text-h4 font-weight-bold">{{ kpi.value }}</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- KPIs por tipo de beneficio -->
      <v-row class="mt-1">
        <v-col cols="12">
          <div class="text-subtitle-2 grey--text text--darken-1 mb-2 px-1">Por tipo de beneficio</div>
        </v-col>
        <v-col v-for="tipo in tipoCards" :key="tipo.label" cols="12" sm="4">
          <v-skeleton-loader v-if="loading" type="card" height="80" />
          <v-card v-else class="pa-3" :style="{ background: tipo.bgColor, color: tipo.textColor, border: '1px solid rgba(0,0,0,0.12)' }">
            <div class="text-subtitle-2">{{ tipo.label }}</div>
            <div class="text-h4 font-weight-bold">{{ tipo.value }}</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Tabla por asesor -->
      <v-row class="mt-3">
        <v-col cols="12">
          <v-card outlined>
            <v-card-title class="py-2 px-4" style="font-size:15px; font-weight:600">
              <v-icon left small color="primary">fa-users</v-icon>
              Desglose por asesor
            </v-card-title>
            <v-skeleton-loader v-if="loading" type="table" />
            <v-data-table
              v-else
              :headers="headersAsesores"
              :items="porAsesor"
              :footer-props="{ itemsPerPageOptions: [10, 25, 50], itemsPerPageText: 'Filas por página:' }"
              no-data-text="Sin datos."
              dense
              class="elevation-0"
            >
              <template #item.aprobado="{ item }">
                <v-chip x-small color="#6C1C19" dark>{{ item.aprobado }}</v-chip>
              </template>
              <template #item.aprobadoConsejo="{ item }">
                <v-chip x-small color="#880C09" dark>{{ item.aprobadoConsejo }}</v-chip>
              </template>
              <template #item.denegado="{ item }">
                <v-chip x-small color="#880C09" dark>{{ item.denegado }}</v-chip>
              </template>
              <template #item.retirado="{ item }">
                <v-chip x-small color="#C6AB55">{{ item.retirado }}</v-chip>
              </template>
              <template #item.anulado="{ item }">
                <v-chip x-small color="#000000" dark>{{ item.anulado }}</v-chip>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

    </v-container>
  </v-card>
</template>

<script>
export default {
  name: 'DashboardSolicitudesBecas',

  props: {
    solicitudes: { type: Array, default: () => [] },
    ciclo:       { type: String, default: '' },
    // Opciones del selector; el cambio se devuelve al padre con update:ciclo
    ciclos:      { type: Array, default: () => [] },
    loading:     { type: Boolean, default: false },
    asesores:    { type: Array, default: () => [] }
  },

  computed: {
    conteo() {
      const s = this.solicitudes
      return {
        total:       s.length,
        pendiente:   s.filter(x => x.estadoCasoId === 1).length,
        enProceso:   s.filter(x => x.estadoCasoId === 2).length,
        aprobado:        s.filter(x => x.estadoCasoId === 3).length,
        denegado:        s.filter(x => x.estadoCasoId === 4).length,
        anulado:         s.filter(x => x.estadoCasoId === 5).length,
        preAprobado:     s.filter(x => x.estadoCasoId === 6).length,
        retirado:        s.filter(x => x.estadoCasoId === 7).length,
        evaluado:        s.filter(x => x.estadoCasoId === 8).length,
        revisado:        s.filter(x => x.estadoCasoId === 9).length,
        aprobadoConsejo: s.filter(x => x.estadoCasoId === 10).length,
      }
    },

    tipoCards() {
      const s = this.solicitudes
      return [
        { label: 'Administrativo',       value: s.filter(x => x.tipoBeneficioId === 1).length, bgColor: '#FFFFFF', textColor: '#000000' },
        { label: 'Servicio Estudiantil', value: s.filter(x => x.tipoBeneficioId === 2).length, bgColor: '#FFFFFF', textColor: '#000000' },
        { label: 'Auspiciado',           value: s.filter(x => x.tipoBeneficioId === 3).length, bgColor: '#FFFFFF', textColor: '#000000' },
      ]
    },

    kpiCards() {
      const c = this.conteo
      return [
        { label: 'Total',                value: c.total,          bgColor: '#FFFFFF', textColor: '#000000' },
        { label: 'Pendiente',            value: c.pendiente,      bgColor: '#FFFFFF', textColor: '#000000' },
        { label: 'En Proceso',           value: c.enProceso,      bgColor: '#FFFFFF', textColor: '#000000' },
        { label: 'Pre-Aprobado',         value: c.preAprobado,    bgColor: '#FFFFFF', textColor: '#000000' },
        { label: 'Evaluado',             value: c.evaluado,       bgColor: '#FFFFFF', textColor: '#000000' },
        { label: 'Revisado',             value: c.revisado,       bgColor: '#FFFFFF', textColor: '#000000' },
        { label: 'Aprobado',             value: c.aprobado,       bgColor: '#FFFFFF', textColor: '#000000' },
        { label: 'Aprobado por Consejo', value: c.aprobadoConsejo,bgColor: '#FFFFFF', textColor: '#000000' },
        { label: 'Denegado',             value: c.denegado,       bgColor: '#FFFFFF', textColor: '#000000' },
        { label: 'Retirado',             value: c.retirado,       bgColor: '#FFFFFF', textColor: '#000000' },
        { label: 'Anulado',              value: c.anulado,        bgColor: '#FFFFFF', textColor: '#000000' },
      ]
    },

    porAsesor() {
      const grupos = new Map()
      for (const s of this.solicitudes) {
        const id = s.asesorId || 'sin-asesor'
        if (!grupos.has(id)) grupos.set(id, [])
        grupos.get(id).push(s)
      }
      return Array.from(grupos.entries()).map(([id, items]) => {
        const asesor = this.asesores.find(a => a.id === id)
        const nombre = asesor?.userClaims?.[0]?.claimValue || id || '—'
        return {
          nombre,
          total:       items.length,
          pendiente:   items.filter(x => x.estadoCasoId === 1).length,
          enProceso:   items.filter(x => x.estadoCasoId === 2).length,
          preAprobado:     items.filter(x => x.estadoCasoId === 6).length,
          retirado:        items.filter(x => x.estadoCasoId === 7).length,
          evaluado:        items.filter(x => x.estadoCasoId === 8).length,
          revisado:        items.filter(x => x.estadoCasoId === 9).length,
          aprobado:        items.filter(x => x.estadoCasoId === 3).length,
          aprobadoConsejo: items.filter(x => x.estadoCasoId === 10).length,
          denegado:        items.filter(x => x.estadoCasoId === 4).length,
          anulado:         items.filter(x => x.estadoCasoId === 5).length,
        }
      }).sort((a, b) => b.total - a.total)
    },

    headersAsesores() {
      return [
        { text: 'Asesor',        value: 'nombre',     class: 'titulo' },
        { text: 'Total',         value: 'total',      class: 'titulo' },
        { text: 'Pendiente',     value: 'pendiente',  class: 'titulo' },
        { text: 'En Proceso',    value: 'enProceso',  class: 'titulo' },
        { text: 'Pre-Aprobado',        value: 'preAprobado',     class: 'titulo' },
        { text: 'Evaluado',            value: 'evaluado',        class: 'titulo' },
        { text: 'Revisado',            value: 'revisado',        class: 'titulo' },
        { text: 'Aprobado',            value: 'aprobado',        class: 'titulo' },
        { text: 'Aprobado por Consejo',  value: 'aprobadoConsejo', class: 'titulo' },
        { text: 'Denegado',            value: 'denegado',        class: 'titulo' },
        { text: 'Retirado',            value: 'retirado',        class: 'titulo' },
        { text: 'Anulado',             value: 'anulado',         class: 'titulo' },
      ]
    }
  }
}
</script>
